import { LOCAL_STORAGE_NAMES } from '../constants/constants';

export interface IUserDashboardPreferences {
  chartVisible?: boolean;
  lastSevenDays?: boolean;
}

type DashboardPreferencesByUser = Record<string, IUserDashboardPreferences>;

const NO_PREFERENCES: IUserDashboardPreferences = {};
const listeners = new Set<() => void>();
let isStorageUnavailable = false;
let memoryState: DashboardPreferencesByUser = {};
let cachedRaw: string | null = null;
let cachedState: DashboardPreferencesByUser = {};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const parseUserPreferences = (value: unknown): IUserDashboardPreferences | undefined => {
  // Early versions stored only the chart visibility, as a plain boolean per user.
  if (typeof value === 'boolean') return { chartVisible: value };
  if (!isPlainObject(value)) return undefined;
  const preferences: IUserDashboardPreferences = {};
  if (typeof value.chartVisible === 'boolean') preferences.chartVisible = value.chartVisible;
  if (typeof value.lastSevenDays === 'boolean') preferences.lastSevenDays = value.lastSevenDays;
  return preferences;
};

const parseDashboardPreferences = (raw: string | null): DashboardPreferencesByUser => {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainObject(parsed)) return {};
    const preferencesByUser: DashboardPreferencesByUser = {};
    Object.entries(parsed).forEach(([userId, value]) => {
      const preferences = parseUserPreferences(value);
      if (preferences) preferencesByUser[userId] = preferences;
    });
    return preferencesByUser;
  } catch {
    return {};
  }
};

const readDashboardPreferences = (): DashboardPreferencesByUser => {
  if (isStorageUnavailable) return memoryState;
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_NAMES.dashboardChartVisible);
    if (raw !== cachedRaw) {
      cachedRaw = raw;
      cachedState = parseDashboardPreferences(raw);
    }
    return cachedState;
  } catch {
    isStorageUnavailable = true;
    return memoryState;
  }
};

const writeDashboardPreferences = (next: DashboardPreferencesByUser) => {
  memoryState = next;
  try {
    const serialized = JSON.stringify(next);
    localStorage.setItem(LOCAL_STORAGE_NAMES.dashboardChartVisible, serialized);
    // Reusing what was just written keeps the references readers hold stable.
    cachedRaw = serialized;
    cachedState = next;
  } catch {
    isStorageUnavailable = true;
  }
  listeners.forEach((listener) => listener());
};

const hasNoPreferences = (preferences: IUserDashboardPreferences) =>
  Object.values(preferences).every((value) => value === undefined);

export const readUserDashboardPreferences = (userId?: string): IUserDashboardPreferences =>
  (userId && readDashboardPreferences()[userId]) || NO_PREFERENCES;

export const updateUserDashboardPreferences = (
  userId: string,
  update: (current: IUserDashboardPreferences) => IUserDashboardPreferences,
) => {
  const state = readDashboardPreferences();
  const current = state[userId] ?? NO_PREFERENCES;
  const next = update(current);
  if (next === current) return;
  const nextState = { ...state, [userId]: next };
  if (hasNoPreferences(next)) delete nextState[userId];
  writeDashboardPreferences(nextState);
};

export const subscribeToDashboardPreferences = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
